import { Appointment, TaskListItem, TechnicianAppointmentsItem, TimeManagementListItem } from '@myapp/contract'
import { useRef, useMemo } from 'react'
import { computeTransfer } from '../model/transferDetection'
import {
  CAL_HOURS,
  CAL_TOTAL_HEIGHT,
  CAL_HEADER_HEIGHT,
  HOUR_HEIGHT,
  CAL_START_HOUR,
  CAL_END_HOUR,
  getHourFromIso,
  getTopPx,
} from '../model/calendarConstants'
import { computeOverlapLayout, getAppointmentRect, getTaskRect, getTMRect, getBreakRect } from '../model/overlapLayout'
import { resolveBreakForDay } from '../../../entities/time-entry/model/useBreakBlock'
import { isWorkingDay } from '../../../shared/lib/timeline/weekHelpers'
import { CurrentTimeLine } from './CurrentTimeLine'
import { WorkerAppointmentBlock } from './WorkerAppointmentBlock'
import { WorkerBreakBlock } from './WorkerBreakBlock'
import { WorkerTMBlock, isAllDayTMEntry } from './WorkerTMBlock'
import { WorkerTaskBlock } from './WorkerTaskBlock'
import type { DragState } from '../model/useCalendarBlockDrag'

const DAY_NAMES = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const MONTH_FORMATTER = new Intl.DateTimeFormat('de-DE', { month: 'short' })

interface Props {
  dayIso: string
  isToday: boolean
  technicianData: TechnicianAppointmentsItem | null
  currentTimePx: number | null
  currentUserId: number
  onAppointmentClick: (id: string) => void
  onTMBlockClick: (entryId: number) => void
  scheduledTasks?: TaskListItem[]
  onTaskClick?: (taskId: number) => void
  drag?: DragState | null
  onBreakPointerDown?: (dayIso: string, e: React.PointerEvent<HTMLDivElement>, gridEl: HTMLElement, blockTopPx: number, anchorRect: DOMRect) => void
  onTMPointerDown?: (entry: TimeManagementListItem, e: React.PointerEvent<HTMLDivElement>, gridEl: HTMLElement, blockTopPx: number, anchorRect: DOMRect) => void
}

function filterForDay(appointments: Appointment[], dayIso: string): Appointment[] {
  return appointments.filter((a) => new Date(a.plannedStart).toISOString().slice(0, 10) === dayIso)
}

function filterTMForDay(entries: TimeManagementListItem[], dayIso: string): TimeManagementListItem[] {
  const dayStart = new Date(`${dayIso}T00:00:00`)
  const dayEnd = new Date(`${dayIso}T23:59:59.999`)
  return entries.filter((e) => new Date(e.startTime) <= dayEnd && new Date(e.endTime) >= dayStart)
}

function filterTasksForDay(tasks: TaskListItem[], dayIso: string): TaskListItem[] {
  return tasks.filter(
    (t) => t.plannedStart && new Date(t.plannedStart).toISOString().slice(0, 10) === dayIso,
  )
}

export const DayColumn = ({
  dayIso,
  isToday,
  technicianData,
  currentTimePx,
  currentUserId,
  onAppointmentClick,
  onTMBlockClick,
  scheduledTasks = [],
  onTaskClick,
  drag = null,
  onBreakPointerDown,
  onTMPointerDown,
}: Props) => {
  const gridRef = useRef<HTMLDivElement>(null)
  const date = new Date(dayIso + 'T12:00:00')
  const dow = date.getDay()
  const dayName = DAY_NAMES[dow]
  const dayNum = date.getDate()
  const monthName = MONTH_FORMATTER.format(date)

  const working = technicianData ? isWorkingDay(technicianData.workingDays, dayIso) : true
  const appointments = technicianData
    ? filterForDay(technicianData.appointments.filter((a) => !a.needsRescheduling), dayIso)
    : []
  const tmEntries = technicianData ? filterTMForDay(technicianData.timeManagementEntries.filter((e) => !e.needsRescheduling), dayIso) : []
  const hasAllDayTM = tmEntries.some((e) => {
    const start = new Date(e.startTime)
    const end = new Date(e.endTime)
    return (
      start.toISOString().slice(0, 10) <= dayIso &&
      start.getHours() === 0 &&
      start.getMinutes() === 0 &&
      end.toISOString().slice(0, 10) >= dayIso &&
      end.getHours() >= 23
    )
  })
  const breakBlock = !hasAllDayTM && technicianData ? resolveBreakForDay(technicianData, dayIso) : null
  const { transferTravel, transferDeparture } = useMemo(() => computeTransfer(appointments), [appointments])
  const dayTasks = filterTasksForDay(scheduledTasks.filter((t) => !t.needsRescheduling), dayIso)

  const overlapLayout = useMemo(() => {
    const rects = [
      ...appointments.map(getAppointmentRect),
      ...dayTasks.flatMap((t) => getTaskRect(t) ?? []),
      ...tmEntries.map((e) => getTMRect(e, dayIso, technicianData?.shiftStart, technicianData?.shiftEnd)),
      ...(working && breakBlock ? [getBreakRect('break', breakBlock.startTimeIso)] : []),
    ]
    return computeOverlapLayout(rects)
  }, [appointments, dayTasks, tmEntries, dayIso, technicianData?.shiftStart, technicianData?.shiftEnd, working, breakBlock])

  const shiftStartHour = technicianData?.shiftStart ? getHourFromIso(technicianData.shiftStart) : null
  const shiftEndHour = technicianData?.shiftEnd ? getHourFromIso(technicianData.shiftEnd) : null

  const preShiftHeight =
    shiftStartHour != null && shiftStartHour > CAL_START_HOUR
      ? (shiftStartHour - CAL_START_HOUR) * HOUR_HEIGHT
      : 0

  const postShiftTop =
    shiftEndHour != null && shiftEndHour < CAL_END_HOUR
      ? (shiftEndHour - CAL_START_HOUR) * HOUR_HEIGHT
      : CAL_TOTAL_HEIGHT

  return (
    <div className="flex flex-col flex-1 min-w-0 border-l border-gray-200">
      {/* Sticky day header */}
      <div
        className={`sticky top-0 z-50 flex flex-col items-center py-2 border-b border-gray-200 ${
          isToday ? 'bg-blue-50' : 'bg-white'
        }`}
        style={{ height: CAL_HEADER_HEIGHT }}
      >
        <span className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
          {dayName}
        </span>
        <span
          className={`text-2xl font-bold leading-tight ${
            isToday ? 'text-blue-700' : 'text-gray-800'
          }`}
        >
          {dayNum}
        </span>
        <span className="text-xs text-gray-400">{monthName}</span>
      </div>

      {/* Time track — carries data-planning-grid for cross-day drag detection */}
      <div
        ref={gridRef}
        className="relative"
        style={{ height: CAL_TOTAL_HEIGHT }}
        data-planning-grid="true"
        data-day-iso={dayIso}
      >
        {CAL_HOURS.map((h, i) => (
          <div
            key={h}
            className="absolute left-0 right-0 border-t border-gray-100"
            style={{ top: i * HOUR_HEIGHT }}
          />
        ))}

        {!working ? (
          <div className="absolute inset-0 bg-gray-300/50 z-[1]" />
        ) : (
          <>
            {preShiftHeight > 0 && (
              <div
                className="absolute left-0 right-0 top-0 bg-gray-300/50 z-[1]"
                style={{ height: preShiftHeight }}
              />
            )}
            {postShiftTop < CAL_TOTAL_HEIGHT && (
              <div
                className="absolute left-0 right-0 bottom-0 bg-gray-300/50 z-[1]"
                style={{ top: postShiftTop }}
              />
            )}
          </>
        )}

        {appointments.map((appt) => {
          const layout = overlapLayout.get(appt.id)
          return (
            <WorkerAppointmentBlock
              key={appt.id}
              appointment={appt}
              isTransferTravel={transferTravel.has(appt.id)}
              isTransferDeparture={transferDeparture.has(appt.id)}
              onClick={() => onAppointmentClick(appt.id)}
              left={layout?.left ?? '2px'}
              zIndex={layout?.zIndex ?? 10}
            />
          )
        })}

        {dayTasks.map((task) => {
          const layout = overlapLayout.get(String(task.id))
          return (
            <WorkerTaskBlock
              key={task.id}
              task={task}
              onClick={() => onTaskClick?.(task.id)}
              left={layout?.left ?? '2px'}
              zIndex={layout?.zIndex ?? 10}
            />
          )
        })}

        {tmEntries.map((entry) => {
          const isAssigned = entry.technicians.some((t) => t.id === currentUserId)
          const isInteractive =
            isAssigned &&
            entry.category === 'PERSONAL_LEAVE' &&
            !isAllDayTMEntry(entry, dayIso) &&
            !entry.needsRescheduling
          const layout = overlapLayout.get(String(entry.id))
          const isTMDragging = drag?.type === 'tm' && drag.entryId === entry.id && drag.dayIso === dayIso
          // Only hide in the column the drag started from, not every non-hover column
          const isDraggedAway = drag?.type === 'tm' && drag.entryId === entry.id && drag.originDayIso === dayIso && drag.dayIso !== dayIso

          if (isDraggedAway) return null

          return (
            <WorkerTMBlock
              key={entry.id}
              entry={entry}
              dayIso={dayIso}
              shiftStart={technicianData?.shiftStart ?? null}
              shiftEnd={technicianData?.shiftEnd ?? null}
              onClick={!isInteractive && isAssigned ? () => onTMBlockClick(entry.id) : undefined}
              left={layout?.left ?? '2px'}
              zIndex={layout?.zIndex ?? 10}
              interactive={isInteractive}
              isDragging={isTMDragging}
              overrideTopPx={isTMDragging ? drag.topPx : undefined}
              onPointerDown={
                isInteractive
                  ? (e) => {
                      const gridEl = gridRef.current
                      if (!gridEl) return
                      const anchorRect = e.currentTarget.getBoundingClientRect()
                      const blockTopPx = getTopPx(
                        new Date(
                          Math.max(new Date(entry.startTime).getTime(), new Date(`${dayIso}T00:00:00`).getTime()),
                        ).toISOString(),
                      )
                      onTMPointerDown?.(entry, e, gridEl, blockTopPx, anchorRect)
                    }
                  : undefined
              }
            />
          )
        })}

        {working && breakBlock && (() => {
          const layout = overlapLayout.get('break')
          const isBreakDragging = drag?.type === 'break' && drag.dayIso === dayIso
          // Only hide in the origin column, not every non-hover column
          const isBreakDraggedAway = drag?.type === 'break' && drag.originDayIso === dayIso && drag.dayIso !== dayIso
          if (isBreakDraggedAway) return null

          return (
            <WorkerBreakBlock
              startTimeIso={breakBlock.startTimeIso}
              overrideTopPx={isBreakDragging ? drag.topPx : undefined}
              left={layout?.left ?? '2px'}
              zIndex={layout?.zIndex ?? 10}
              interactive
              isDragging={isBreakDragging}
              onPointerDown={(e) => {
                const gridEl = gridRef.current
                if (!gridEl) return
                const anchorRect = e.currentTarget.getBoundingClientRect()
                onBreakPointerDown?.(dayIso, e, gridEl, getTopPx(breakBlock.startTimeIso), anchorRect)
              }}
            />
          )
        })()}

        {isToday && currentTimePx !== null && (
          <CurrentTimeLine topPx={currentTimePx} />
        )}
      </div>
    </div>
  )
}
